<template>
  <v-card>
    <v-dialog v-model="recallDialog" max-width="500px">
      <RecallForm
        :saleId=saleId
        @close="onCloseRecall"
      />
    </v-dialog>
    <v-dialog v-model="isRecallsOpen" max-width="500px">
      <Recalls v-if="isRecallsOpen" :saleId=saleId />
    </v-dialog>
    <v-card-title class="d-flex flex-row align-end">
      <v-btn
        color="primary"
        class="mr-4"
        to="sales/add"
        rounded
      >
        Add Sale
      </v-btn>
      <v-btn icon text @click="getItems">
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Search"
        single-line
        hide-details
        clearable
        dense
        filled
        outlined
        rounded
      ></v-text-field>
      <v-btn icon color="primary" @click="exportCSV"><v-icon>download</v-icon></v-btn>
      <div class="d-flex flex-row ml-5" v-if="showWarranties">
        <v-card
          flat
          outlined=""
          height="100"
          class="d-flex pa-5 mr-1 align-center justify-center"
          @click="$router.push(`warrantied-sales`)"
        >
          <div class="d-flex align-start">
            <h1 class="purple--text mr-2 text-h1" style="line-height:1">{{remainingWarranties}}</h1>
            <h3 class="text-h5 mt-2">Active Warranties</h3>
          </div>
        </v-card>
      </div>
    </v-card-title>
    <v-data-table 
      :headers="headers"
      :items="list" 
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="options"
    >
      <template v-slot:item.date="{ item }">
        {{$date.get(item.date, "DD MMM YYYY")}}
      </template>
      <template v-slot:item.receipt="{ item }">
        <a :href="item.receipt" download="sales_receipt.pdf">Receipt</a>
      </template>
      <template v-slot:item.warranty_remaining="{ item }">
        <span v-if="item.warranty_remaining > 0">
          {{item.warranty_remaining}} days
        </span>
      </template>
      <template v-slot:item.recall_count="{ item }">
        <v-btn icon outlined rounded @click="openRecallList(item.id)">
          {{item.recall_count}}
        </v-btn>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn icon text class="mr-1" :to="`/sales/${item.id}/recall`">
          <v-icon small>phone</v-icon>+
        </v-btn>
        <v-btn icon text :to="`sales/edit/${item.id}`" class="mr-1">
          <v-icon small>edit</v-icon>
        </v-btn>
        <v-btn icon text @click="deleteItem(item)" color="red">
          <v-icon small>delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import api from "@/api";
import RecallForm from "./RecallForm";
import Recalls from "./Recalls";
import { download } from "../../directive/ExportToCSV";

export default {
  name: "Sales",
  props: {
    fetchItems: {
      default: () => ({})
    },
    showWarranties: {
      default: true
    }
  },
  components: {
    RecallForm,
    Recalls
  },
  data: () => ({
    totalItems: 0,
    remainingWarranties: 0,
    dialog: false,
    loading: false,
    options: {},
    headers: [
      {
        text: '#',
        align: 'left',
        sortable: false,
        value: 'id',
      },
      { text: 'Sold On', value: 'date' },
      { text: 'Name', value: 'fullname' },
      { text: 'Phone', value: 'phone' },
      { text: 'Make', value: 'make' },
      { text: 'Year', value: 'year' },
      { text: 'Total', value: 'total' },
      { text: 'Receipt', value: 'receipt' },
      { text: 'Vin', value: 'vin' },
      { text: 'Warranty Remaining', value: 'warranty_remaining' },
      { text: 'Recalls', value: 'recall_count' },
      { text: 'Actions', value: 'action', sortable: false },
    ],
    list: [],
    editedIndex: -1,
    editedItem: {
      name: '',
      calories: 0,
      fat: 0,
      carbs: 0,
      protein: 0,
    },
    defaultItem: {
      name: '',
      calories: 0,
      fat: 0,
      carbs: 0,
      protein: 0,
    },
    search: '',
    recallDialog: false,
    isRecallsOpen: false,
    rules: {
      required: v => !!v || 'required'
    },
    saleId: null
  }),

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    options: {
      handler () {
        this.getItems()
      },
      deep: true,
    },
    search() {
      this.getItems()
    },
    recall:{
      handler() {
        this.dateFormatted = this.formatDate(this.recall.date);
      },
      deep: true
    }
  },
  methods: {
    initialize () {
      this.getItems()
    },
    formatDate (date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },

    async getItems() {
      const { page = 0, itemsPerPage = 10 } = this.options
      this.loading = true;
      const response = await this.fetchItems(this.search, itemsPerPage, page);
      this.totalItems = response.data.count;
      this.remainingWarranties = response.data.remainingWarranties;
      this.list = response.data.data;
      this.loading = false;
    },
    async deleteItem (item) {
      const confirm = await this.$alert.confirm();
      if(confirm && confirm.value === true) {
        try {
          await api.deleteSale([item.id]);
          this.getItems()
          this.$toast.success({ title: "Deleted Successfully" })
        } catch (error) {
          this.$toast.error({ title: "Error Deleting" })
        }
      } 
    },

    close () {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },

    save () {
      if (this.editedIndex > -1) {
        Object.assign(this.desserts[this.editedIndex], this.editedItem)
      } else {
        this.desserts.push(this.editedItem)
      }
      this.close()
    },
    openRecallList(id) {
      this.isRecallsOpen = true;
      this.saleId = id;
    },
    openRecall(id) {
      // this.recallDialog = true;
      // this.saleId = id;
      this.$router.push("")
    },
    onCloseRecall() {
      this.recallDialog = false;
      this.saleId = null;
    },
    async exportCSV() {
      const response = await api.getSaleCSV();
      download(response.data);
    }
  }
};
</script>